import { Action, Reducer } from "redux";
import { CharacterInitializer, LocalCharacterInitializer } from "../../../../entities/characters/Character";
import { CharacterAction, CharacterReceivePlayerRoll, CharacterSetPlayerRolling } from "./CharacterStore.Actions";
import { CharacterDefaultState, CharacterState } from "./CharacterStore.State";
import { AdventureCharacterAdded, AdventureCharacterRemoved } from "../../adventures/AdventureStore.Actions";
import { CharacterSpecificReducers } from "./reducers/CharacterSpecificReducers";
import { CharacterCompetencyReducers } from "./reducers/CharacterCompetencyReducers";
import { ItemFilter } from "../../../../entities/filters/ItemFilter";
import { CharacterConditionReducers } from "./reducers/CharacterConditionReducers";
import { CharacterItemReducers } from "./reducers/CharacterItemReducers";
import { CharacterMinionReducers } from "./reducers/CharacterMinionReducer";
import { CharacterPerkReducers } from "./reducers/CharacterPerkReducers";
import { CharacterPersonalityReducers } from "./reducers/CharacterPersonalityReducers";
import { CharacterRhetoricReducers } from "./reducers/CharacterRhetoricReducers";
import { CharacterSkillReducers } from "./reducers/CharacterSkillReducers";
import { CharacterPropertyReducers } from "./reducers/CharacterPropertyReducers";
import { CharacterReducerHelpers } from "./reducers/CharacterReducerHelpers";

export const CharacterReducer: Reducer<CharacterState> = (
    state: CharacterState | undefined,
    incomingAction: Action
): CharacterState => {
    if (state == undefined) {
        return CharacterDefaultState;
    }
    let character: CharacterInitializer;
    let characters: LocalCharacterInitializer[];
    const action = incomingAction as CharacterAction | AdventureCharacterAdded | AdventureCharacterRemoved;
    switch (action.type) {
        case 'CHARACTERS_LOADED':
            return { ...state, loaded: true, characters: ConvertToDeletableCharacters(...action.characters) };
        case 'CHARACTER_SET_CREATING':
            return { ...state, creating: action.creating };
        case 'CHARACTERS_UNOWNED_LOADED':
            return { ...state, otherPlayerCharacters: [...state.otherPlayerCharacters, ...ConvertToDeletableCharacters(...action.characters)].unique(c => c.id) }
        case 'CHARACTER_UNOWNED_RECEIVED':
            return { ...state, otherPlayerCharacters: [...state.otherPlayerCharacters, ...ConvertToDeletableCharacters(action.character)].unique(c => c.id)}
        case 'CHARACTER_ADD_NEW':
            characters = ConvertToDeletableCharacters(...state.characters, action.character);
            return { ...state, characters };
        case 'CHARACTER_UPDATED':
            character = action.character;
            characters = [...state.characters.filter(c => c.id == character.id), ...ConvertToDeletableCharacters(character)];
            return { ...state, characters };
        case 'CHARACTER_DELETING':
            return { ...state, characters: state.characters.map(c => ({ ...c, deleting: c.id == action.id ? !action.undo : c.deleting })) };
        case 'CHARACTER_DELETED':
            return { ...state, characters: state.characters.filter(c => c.id != action.id) };

        case 'CHARACTER_SET_PLAYER_ROLLING': return setPlayerRolling(state, action);
        case 'CHARACTER_RECEIVE_PLAYER_ROLL': return receivePlayerRoll(state, action);

        case 'CHARACTER_SET_PENDING':
            return CharacterSpecificReducers.setEntityPending(state, action);
        case 'CHARACTER_SET_PREVIEW_ITEM_INSTANCE_ID':
            return CharacterSpecificReducers.previewItem(state, action);
        case 'CHARACTER_SET_PREVIEW_SLOT_ID':
            return CharacterSpecificReducers.previewSlot(state, action);
        case 'CHARACTER_SHOW_ITEM_ADDER':
            return CharacterSpecificReducers.showItemAdder(state, action);
        case 'CHARACTER_HIDE_ITEM_ADDER':
            return CharacterSpecificReducers.hideItemAdder(state, action);
        case 'CHARACTER_FILTER_ITEMS':
            return CharacterSpecificReducers.filterItems(state, action);
        case 'CHARACTER_SET_HAND':
            return CharacterSpecificReducers.setHand(state, action);
        case 'CHARACTER_SET_ROLLING':
            return CharacterSpecificReducers.setRolling(state, action);
        case 'CHARACTER_RESET_HAND':
            return CharacterSpecificReducers.resetHand(state, action);
        case 'CHARACTER_SET_COMMUNAL_DICE':
            return CharacterSpecificReducers.setCommunalDice(state, action);
        case 'CHARACTER_SET_ROLL_SKILL':
            return CharacterSpecificReducers.setRollSkill(state, action);
        case 'CHARACTER_CLEAR_ROLL_SKILL':
            return CharacterSpecificReducers.clearRollSkill(state, action);


        //Properties
        case 'CHARACTER_SET_RULE_ENFORCEMENT':
            return CharacterPropertyReducers.setRuleEnforcement(state, action);
        case 'CHARACTER_UPDATE_NAME':
            return CharacterPropertyReducers.updateName(state, action);
        case 'CHARACTER_UPDATE_KINSHIP':
            return CharacterPropertyReducers.updateKinship(state, action);
        case 'CHARACTER_UPDATE_LEVEL':
            return CharacterPropertyReducers.updateLevel(state, action);
        case 'CHARACTER_UPDATE_CLASS_NAME':
            return CharacterPropertyReducers.updateClassName(state, action);
        case 'CHARACTER_ADJUST_MOVEMENT':
            return CharacterPropertyReducers.adjustMovement(state, action);
        case 'CHARACTER_OVERRIDE_MOVEMENT':
            return CharacterPropertyReducers.overrideMovement(state, action);
        case 'CHARACTER_UPDATE_HEALTH':
            return CharacterPropertyReducers.updateHealth(state, action);
        case 'CHARACTER_SET_JUICED':
            return CharacterPropertyReducers.setJuiced(state, action);
        case 'CHARACTER_UPDATE_MONEY':
            return CharacterPropertyReducers.updateMoney(state, action);
        case 'CHARACTER_PENDING_PROPERTY':
            return CharacterPropertyReducers.setPendingProperty(state, action);
        case 'CHARACTER_PENDING_PROPERTIES':
            return CharacterPropertyReducers.setPendingProperties(state, action);
        case 'CHARACTER_SAVE_PROPERTY':
            return CharacterPropertyReducers.setSavedProperty(state, action);
        case 'CHARACTER_SAVE_PROPERTIES':
            return CharacterPropertyReducers.setSavedProperties(state, action);


        //Competencies
        case 'CHARACTER_ADD_COMPETENCY':
            return CharacterCompetencyReducers.addCompetency(state, action);
        case 'CHARACTER_ADD_CUSTOM_COMPETENCY':
            return CharacterCompetencyReducers.addCustomCompetency(state, action);
        case 'CHARACTER_REMOVE_COMPETENCY':
            return CharacterCompetencyReducers.removeCompetency(state, action);
        case 'CHARACTER_SET_COMPETENCY_LEVEL':
            return CharacterCompetencyReducers.setCompetencyLevel(state, action);
            
        //Conditions
        case 'CHARACTER_ADD_CONDITION':
            return CharacterConditionReducers.addCondition(state, action);
        case 'CHARACTER_REMOVE_CONDITION':
            return CharacterConditionReducers.removeCondition(state, action);

        //Items
        case 'CHARACTER_ADD_ITEM':
            return CharacterItemReducers.addItem(state, action);
        case 'CHARACTER_MOVE_ITEMS':
            return CharacterItemReducers.moveItems(state, action);
        case 'CHARACTER_REMOVE_ITEMS':
            return CharacterItemReducers.removeItems(state, action);
        case 'CHARACTER_SAVE_ITEM':
            return CharacterItemReducers.saveItem(state, action);

        case 'CHARACTER_UPDATE_ITEM_NAME':
            return CharacterItemReducers.updateItemName(state, action);
        case 'CHARACTER_UPDATE_ITEM_NOTES':
            return CharacterItemReducers.updateItemNotes(state, action);

        case 'CHARACTER_ASSIGN_MOD':
            return CharacterItemReducers.assignMod(state, action);
        case 'CHARACTER_REMOVE_MOD':
            return CharacterItemReducers.removeMod(state, action);

        case 'CHARACTER_ADD_SLOTS':
            return CharacterItemReducers.addSlots(state, action);
        case 'CHARACTER_REMOVE_SLOTS':
            return CharacterItemReducers.removeSlots(state, action);
        case 'CHARACTER_UPDATE_SLOTS':
            return CharacterItemReducers.updateSlots(state, action);

        case 'CHARACTER_ASSIGN_BODY_MOD':
            return CharacterItemReducers.assignBodyMod(state, action);
        case 'CHARACTER_REMOVE_BODY_MOD':
            return CharacterItemReducers.removeBodyMod(state, action);


        //Minions
        case 'CHARACTER_SAVE_MINION':
            return CharacterMinionReducers.saveMinion(state, action);
        case 'CHARACTER_ADD_MINION':
            return CharacterMinionReducers.addMinion(state, action);
        case 'CHARACTER_REMOVE_MINION':
            return CharacterMinionReducers.removeMinion(state, action);
        case 'CHARACTER_UPDATE_MINION':
            return CharacterMinionReducers.updateMinion(state, action);



        //Perks
        case 'CHARACTER_SAVE_PERK':
            return CharacterPerkReducers.savePerk(state, action);
        case 'CHARACTER_ADD_PERK':
            return CharacterPerkReducers.addPerk(state, action);
        case 'CHARACTER_REMOVE_PERK':
            return CharacterPerkReducers.removePerk(state, action);


        //Personalities
        case 'CHARACTER_SAVE_PERSONALITY':
            return CharacterPersonalityReducers.savePersonality(state, action);
        case 'CHARACTER_ADD_PERSONALITY':
            return CharacterPersonalityReducers.addPersonality(state, action);
        case 'CHARACTER_REMOVE_PERSONALITY':
            return CharacterPersonalityReducers.removePersonality(state, action);


        //Rhetorics
        case 'CHARACTER_SAVE_RHETORIC':
            return CharacterRhetoricReducers.saveRhetoric(state, action);
        case 'CHARACTER_UPDATE_RHETORIC_PRIORITY':
            return CharacterRhetoricReducers.updateRhetoric(state, action);
        case 'CHARACTER_ADJUST_RHETORIC':
            return CharacterRhetoricReducers.adjustRhetoric(state, action);
        case 'CHARACTER_OVERRIDE_RHETORIC':
            return CharacterRhetoricReducers.overrideRhetoric(state, action);

            
        //Skills
        case 'CHARACTER_SAVE_SKILL':
            return CharacterSkillReducers.saveSkill(state, action);
        case 'CHARACTER_UPDATE_SKILL':
            return CharacterSkillReducers.updateSkill(state, action);
        case 'CHARACTER_ADJUST_SKILL':
            return CharacterSkillReducers.adjustSkill(state, action);
        case 'CHARACTER_OVERRIDE_SKILL':
            return CharacterSkillReducers.overrideSkill(state, action);
            

        default: return { ...state };
    }
};

const ConvertToDeletableCharacters = (...characters: CharacterInitializer[]): LocalCharacterInitializer[] => {
    return characters.map(c => ({
        ...c,
        deleting: false,
        pendingProperties: {},
        showItemAdder: false,
        itemFilter: ItemFilter.GetDefaultInitializer(),
        communalDice: [1,1]
    }));
}

const setPlayerRolling = (state: CharacterState, action: CharacterSetPlayerRolling): CharacterState => {
    const { characterId, request, response } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return setOtherPlayerRolling(state, action);
    }

    const newCharacter = {
        ...character,
        currentDice: {
            ...character.currentDice,
            actionDice: response.actionDice,
            extraDice: response.extraDice,
            skillId: request?.skillId ?? undefined,
        },
        rollRequest: action.request ?? undefined
    }
    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const setOtherPlayerRolling = (state: CharacterState, action: CharacterSetPlayerRolling): CharacterState => {
    const { characterId, request, response } = action;
    const character = state.otherPlayerCharacters.find(c => c.id == characterId);
    if (character == null) {
        return state;
    }
    
    const newCharacter = {
        ...character,
        currentDice: {
            ...character.currentDice,
            actionDice: response.actionDice,
            extraDice: response.extraDice,
            skillId: request?.skillId ?? undefined,
        },
        rollRequest: action.request ?? undefined
    }
    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const receivePlayerRoll = (state: CharacterState, action: CharacterReceivePlayerRoll): CharacterState => {
    const { characterId, response } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return receiveOtherPlayerRoll(state, action);
    }

    const newCharacter = { ...character };
    delete newCharacter.rollRequest;
    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const receiveOtherPlayerRoll = (state: CharacterState, action: CharacterReceivePlayerRoll): CharacterState => {
    const { characterId, response } = action;
    const character = state.otherPlayerCharacters.find(c => c.id == characterId);
    if (character == null) {
        return state;
    }

    const newCharacter = { ...character };
    delete newCharacter.rollRequest;
    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}